import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Receipt, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import apiClient from '../../utils/apiClient';
import { triggerBacklogRefresh } from './GlobalBacklogModalProvider';
import { isIOSNative } from '../../utils/nativeBridge';

/**
 * iter1207 - RetryBacklogConfirmButton
 *
 * Shows "Retry Backlog Confirmation" beneath a bank account tile when the
 * user previously dismissed the backlog confirmation modal (or the initial
 * confirmation attempt did not complete for any other reason).
 *
 * Detection strategy: an unpaid pending backlog exists for the tile's
 * connection in /plaid/pending-backlogs (or /mono/pending-backlogs). This is
 * a distinct signal from RetryBacklogButton, which handles the
 * `charge_failed` post-charge failure case only.
 *
 * Clicking the button re-opens the GLOBAL backlog modal by dispatching the
 * refresh event.
 */
export default function RetryBacklogConfirmButton({
  bankAccountId,
  connectionId,
  provider = 'plaid', // 'plaid' | 'mono'
  bankName = 'Bank',
}) {
  // iOS App Store build: fee-confirmation UI must never render in-app.
  if (isIOSNative()) {
    return null;
  }
  return (
    <RetryBacklogConfirmButtonInner
      bankAccountId={bankAccountId}
      connectionId={connectionId}
      provider={provider}
      bankName={bankName}
    />
  );
}

function RetryBacklogConfirmButtonInner({
  bankAccountId,
  connectionId,
  provider = 'plaid', // 'plaid' | 'mono'
  bankName = 'Bank',
}) {
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reopening, setReopening] = useState(false);

  const check = useCallback(async () => {
    if (!connectionId) { setLoading(false); return; }
    try {
      const url = provider === 'mono' ? '/mono/pending-backlogs' : '/plaid/pending-backlogs';
      const { data } = await apiClient.get(url);
      const match = (data?.pending_backlogs || []).find(
        (r) => r.connection_id === connectionId,
      );
      setPending(match || null);
    } catch (_) {
      setPending(null);
    } finally {
      setLoading(false);
    }
  }, [connectionId, provider]);

  useEffect(() => {
    check();
    // Refresh whenever the global provider signals a change
    const onBus = () => check();
    window.addEventListener('akountfly:backlog-refresh', onBus);
    // Also re-check every 60s in case status changed via a webhook or worker
    const t = setInterval(check, 60_000);
    return () => {
      window.removeEventListener('akountfly:backlog-refresh', onBus);
      clearInterval(t);
    };
  }, [check]);

  const openModal = async () => {
    setReopening(true);
    // Clear the outstanding-tasks skip row (best-effort) so the modal
    // doesn't get instantly re-suppressed by the provider session cache.
    try {
      const { data } = await apiClient.get('/banking/outstanding-tasks');
      const t = (data?.tasks || []).find(
        (r) => r.provider === provider && r.connection_id === connectionId,
      );
      if (t?.id) {
        await apiClient.post(`/banking/outstanding-tasks/${t.id}/dismiss`);
      }
    } catch (_) { /* non-fatal */ }
    triggerBacklogRefresh();
    setReopening(false);
  };

  if (loading || !pending) return null;

  const feeStr = pending.symbol && pending.backlog_fee != null
    ? `${pending.symbol}${Number(pending.backlog_fee).toFixed(2)}`
    : null;

  return (
    <div
      className="mt-2 border border-teal-300 bg-teal-50 rounded-md p-2.5 flex items-center gap-2 text-sm"
      data-testid={`retry-backlog-confirm-card-${bankAccountId || connectionId}`}
    >
      <Receipt className="w-4 h-4 text-teal-700 flex-shrink-0" />
      <div className="flex-1 text-teal-900">
        <div className="font-semibold">
          Backlog fee awaiting confirmation for {bankName}
          {feeStr ? ` (${feeStr})` : ''}
        </div>
        <div className="text-xs opacity-80">
          {pending.backlog_tx_count} historical transactions are ready to import once you confirm the fee.
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={openModal}
        disabled={reopening}
        className="border-teal-500 text-teal-900 hover:bg-teal-100"
        data-testid={`retry-backlog-confirm-btn-${bankAccountId || connectionId}`}
      >
        {reopening ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Opening…</>
        ) : (
          <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry Backlog Confirmation</>
        )}
      </Button>
    </div>
  );
}
